import { Accordion, Button, Heading, List, Modal, ModalProps } from '@boltenergy-be/design-system'
import { Trans, useTranslation } from 'next-i18next/pages'
import parse from 'html-react-parser'
import Link from 'components/link/Link'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { setShowPlentyPropositions } from 'store/customer/slice'
import { PLENTY_INFO_NOTE } from 'constants/constants'
import styles from './PlentyModal.module.scss'
import { trackEvent } from 'utils/tracking'
import { PLENTY_PRODUCTS, Product } from '@boltenergy-be/shared'
import { AccordionItem } from '@boltenergy-be/design-system'
import classNames from 'classnames'
import { useGetLanguage } from 'hooks/useGetLanguage'

const PlentyModal = ({ isOpen, onRequestClose, enforcedPlenty }: ModalProps & { enforcedPlenty?: boolean }) => {
  // Redux
  const { propositions } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('simulation')
  const language = useGetLanguage()

  // Constants
  const context = enforcedPlenty ? 'enforced' : 'default'

  return (
    <Modal
      className={classNames({ [styles['enforced-modal']]: enforcedPlenty })}
      title={
        <Heading as="h1" variant="h4">
          {t('proposition.plenty.modal.title', { context })}
        </Heading>
      }
      {...{ isOpen, onRequestClose }}
    >
      <p>{parse(t('proposition.plenty.modal.description', { context }))}</p>

      {!enforcedPlenty && (
        <section>
          <Heading as="h2" variant="h5" serif className="mb-200">
            {t('proposition.plenty.modal.subtitle')}
          </Heading>
          <List as="ul">
            {(t('proposition.plenty.modal.advantages', { returnObjects: true }) as string[]).map((item, index) => (
              <li key={`advantage-${index}`}>{item}</li>
            ))}
          </List>
        </section>
      )}

      <section className={styles.comparison}>
        <div className="flex-col gap-200">
          <span>{t('proposition.plenty.modal.investWithUs')}</span>
          <strong className="h4">{t('proposition.plenty.modal.price', { amount: 250 })}</strong>
        </div>

        <div className="flex-col gap-200">
          <span>{t('proposition.plenty.modal.maxReturn')}</span>
          <strong className="h4">{t('proposition.plenty.modal.perYear', { amount: 135 })}</strong>
        </div>
      </section>

      <small className="text-subdued">
        <Trans
          t={t}
          i18nKey="proposition.plenty.modal.warning"
          ns="simulation"
          components={[<Link as="a" variant="inline" download href={PLENTY_INFO_NOTE[language]} />]}
        />
      </small>

      {enforcedPlenty ? (
        <footer className={styles['faq-footer']}>
          <Heading as="h2" variant="h4">
            {t('proposition.plenty.modal.faqTitle')}
          </Heading>
          <Accordion className={styles.accordion} items={t('proposition.plenty.modal.faqs', { returnObjects: true }) as AccordionItem[]} />
        </footer>
      ) : (
        <footer className={styles.footer}>
          <Button
            size="large"
            onClick={() => {
              dispatch(setShowPlentyPropositions(true))
              trackEvent({ category: 'simulation', object: 'plenty_propositions', action: 'view' })

              if (
                !propositions.simulatedPropositions ||
                (propositions.simulatedPropositions &&
                  !Object.keys(propositions.simulatedPropositions).some((simulatedProduct: Product) =>
                    PLENTY_PRODUCTS.some((product) => product === simulatedProduct)
                  ))
              ) {
                requestAndSavePropositions({ products: PLENTY_PRODUCTS })
              }

              onRequestClose()
            }}
          >
            {t('proposition.plenty.modal.actions.showPlentyTariffs')}
          </Button>

          <Button
            size="large"
            variant="secondary"
            onClick={() => {
              dispatch(setShowPlentyPropositions(false))
              trackEvent({ category: 'simulation', object: 'plenty_propositions', action: 'remove' })
              onRequestClose()
            }}
          >
            {t('proposition.plenty.modal.actions.keepBoltTariffs')}
          </Button>
        </footer>
      )}
    </Modal>
  )
}

export default PlentyModal
